<template>
  <flex-column class="filter-builder">
    <flex-body>
      <div class="filters">
        <div
          class="top"
          v-if="model && model.filters && model.filters.length > 1"
        >
          <div class="summary">
            Match
            <native-select v-model="model.operator" :field="operatorField">
              {{ summary }}
            </native-select>
            of the following rules
          </div>
        </div>
        <!-- :enableRemove="model.filters.length > 1" -->
        <filter-rule
          enableRemove
          :key="`rule-${index}-${rule.guid}`"
          @remove="removeRule(index)"
          :definition="definition"
          v-model="model.filters[index]"
          :index="index"
          v-for="(rule, index) in model.filters"
        />
        <ux-button size="sm" @click="addRule()">Add Filter Rule</ux-button>
      </div>
    </flex-body>
  </flex-column>
</template>
<script>
import NativeSelect from "../form/inputs/select.vue";

import FilterRule from "./FilterRule.vue";
import debounce from "lodash/debounce";

// The builder renders each top-level `filters[]` entry as a RULE (a group).
// A canonical/flat filter — as produced by the API, the AI assistant, imports,
// or hand-authored JSON — puts bare conditions directly under the top group.
// Binding a bare condition to a FilterRule mis-renders it (and FilterRule's
// lifecycle stamps `operator`/`filters` onto the leaf, corrupting it). Detect
// the flat shape (every top-level entry is a condition, i.e. has no nested
// `filters[]`) and wrap the whole set in a single rule so it renders as
// condition rows and round-trips cleanly. Already-nested filters pass through.
function toBuilderShape(filter) {
  if (!filter || typeof filter !== "object" || !Array.isArray(filter.filters)) {
    return filter;
  }
  const entries = filter.filters;
  const allConditions =
    entries.length > 0 &&
    entries.every(
      (e) => e && typeof e === "object" && !Array.isArray(e.filters),
    );
  if (!allConditions) return filter;
  const operator = filter.operator || "and";
  return { ...filter, operator, filters: [{ operator, filters: entries }] };
}

export default {
  components: {
    FilterRule,
    NativeSelect,
  },
  methods: {
    removeRule(index) {
      if (!this.model.filters) {
        return;
      }
      this.model.filters.splice(index, 1);
    },
    addRule() {
      const self = this;

      if (!self.model.filters) {
        self.model.filters = [];
      }

      if (!self.model.operator) {
        self.model.operator = "and";
      }

      self.model.filters.push({
        guid: self.$sdk.utils.guid(),
        operator: "and",
        filters: [{}],
      });
    },
  },
  computed: {
    summary() {
      switch (this.model.operator) {
        case "or":
          return `any`;
          break;
        case "nor":
          return `none`;
          break;
        default:
          return `all`;
          break;
      }
    },
    operatorField() {
      return {
        minimum: 1,
        maximum: 1,
        options: [
          {
            title: "All",
            value: "and",
          },
          {
            title: "Any",
            value: "or",
          },
          {
            title: "None",
            value: "nor",
          },
        ],
      };
    },
  },
  watch: {
    modelValue: {
      handler(v) {
        var currentString = JSON.stringify(this.model);
        var stringed = JSON.stringify(toBuilderShape(v));

        if (currentString != stringed) {
          var model = JSON.parse(stringed);

          this.model = model;
        }
      },
      immediate: true,
    },
    model: {
      handler: debounce(function (v) {
        v = JSON.parse(JSON.stringify(v));
        this.$emit("update:modelValue", v);
      }, 100),
      deep: true,
      immediate: true,
    },
  },
  props: {
    definition: {
      type: Object,
      required: true,
    },
    modelValue: {
      type: Object,
      default() {
        return {
          operator: "and",
          filters: [
            {
              operator: "and",
              filters: [],
            },
          ],
        };
      },
    },
  },
  data() {
    return {
      model: toBuilderShape(JSON.parse(JSON.stringify(this.modelValue))),
    };
  },
};
</script>
<style lang="scss" scoped>
.filter-builder {
  .summary {
    font-size: 0.8em;
    opacity: 0.8;
    padding: 0.3em 0.7em;

    .native-select {
      display: inline-block;
      border: 1px solid transparent;
      border-radius: 0.3em;
      padding: 0.2em;

      &:hover {
        background: var(--bg1, #fff);
        border-color: var(--primary);
      }
    }
  }
}
</style>
